/*
 * Distractor pool selection (FR-LOGIC-03).
 *
 * Responsibilities (this module only):
 *   - Build the "eligible pool" of runes the user has been exposed to,
 *     i.e. runes introduced at levels 1 through the user's current
 *     level.
 *   - Sample distractors uniformly at random from that pool, excluding
 *     the correct answer and without replacement (so no distractor
 *     repeats inside one question).
 *   - Gracefully return fewer distractors when the eligible pool is
 *     thin: the caller composes the final option list (correct + N
 *     distractors), so this function never falls below zero and never
 *     pads with placeholders — the floor of "minimum 2 total options
 *     per question" is enforced by the caller via `correct + at least
 *     one distractor` whenever any candidate exists.
 *
 * Pure function: no module-level state, no imports beyond the
 * RuneEntry type. The corpus and currentLevel are inputs supplied by
 * the caller (Exercise A in #16, Exercise B in #19). RNG is injected
 * via `options.random` so tests can pin exact outputs with a seeded
 * sequence; production callers get Math.random for free.
 *
 * Pool interpretation: "all runes at levels 1 through N" is read as
 * the distinct *rune set* introduced by those levels, not every
 * word/line entry. We dedupe `type='word'` entries by `latin` because
 * each level's five word rows are the canonical rune introductions
 * (per CLAUDE.md and the M0-3 corpus convention). Multi-rune line
 * entries are content for Exercise B reading drills, not standalone
 * options on a recognition question.
 */

import type { RuneEntry } from './csv';

export interface DistractorOptions {
  /**
   * Total max options including the correct answer. Default 4 (i.e. 3
   * distractors). The function returns at most `totalOptions - 1`
   * distractors; pass a higher value to support questions with more
   * choices without changing this module.
   */
  totalOptions?: number;
  /**
   * Injectable RNG returning a float in [0, 1). Default Math.random.
   * Tests pass a seeded function so output is deterministic.
   */
  random?: () => number;
}

const DEFAULT_TOTAL_OPTIONS = 4;

/**
 * Pick distractor entries to accompany a correct answer in a multiple-
 * choice question.
 *
 * Returns at most `totalOptions - 1` distractor entries (default 3),
 * uniformly sampled without replacement from the level-eligible pool
 * with the correct answer excluded. May return fewer when the pool is
 * short — the caller assembles the final option list (correct +
 * distractors) and shuffles for display.
 *
 * The function is pure: same inputs + same `random` produce the same
 * output every time.
 */
export function pickDistractors(
  correct: RuneEntry,
  corpus: readonly RuneEntry[],
  currentLevel: number,
  options?: DistractorOptions,
): RuneEntry[] {
  const totalOptions = options?.totalOptions ?? DEFAULT_TOTAL_OPTIONS;
  const random = options?.random ?? Math.random;

  // A question with fewer than 2 total options isn't a multiple-choice
  // question — bail early rather than do partial work.
  if (totalOptions < 2) return [];

  const maxDistractors = totalOptions - 1;
  const pool = buildEligiblePool(corpus, currentLevel, correct.latin);
  if (pool.length === 0) return [];

  return sampleWithoutReplacement(pool, Math.min(maxDistractors, pool.length), random);
}

/**
 * Build the deduped rune set introduced at levels 1..currentLevel,
 * then drop the correct answer. Dedup key is `latin` — the canonical
 * identifier for a rune in the CSV. Only `type='word'` rows are
 * considered because those are the per-level rune introductions
 * (CLAUDE.md: "Each LEVEL introduces 5 new runes (via the type=word
 * rows at that level)"). Short/long-line entries are reading content,
 * not standalone distractor candidates.
 *
 * Order of the returned pool follows first-occurrence order in the
 * corpus, which keeps tests against seeded RNGs stable across runs.
 */
function buildEligiblePool(
  corpus: readonly RuneEntry[],
  currentLevel: number,
  excludeLatin: string,
): RuneEntry[] {
  const seen = new Set<string>();
  const pool: RuneEntry[] = [];
  for (const entry of corpus) {
    if (entry.type !== 'word') continue;
    if (entry.level > currentLevel) continue;
    if (entry.latin === excludeLatin) continue;
    if (seen.has(entry.latin)) continue;
    seen.add(entry.latin);
    pool.push(entry);
  }
  return pool;
}

/**
 * Sample `count` items uniformly without replacement from `source`.
 * Uses a partial Fisher-Yates: pick a random index in the remaining
 * range, swap it to the front, advance the boundary. O(count) time,
 * no allocation beyond the working copy and the result.
 *
 * Caller guarantees `count <= source.length` (we cap at the call site
 * above), so the loop never runs out of candidates.
 */
function sampleWithoutReplacement<T>(
  source: readonly T[],
  count: number,
  random: () => number,
): T[] {
  const working = source.slice();
  const picked: T[] = [];
  for (let i = 0; i < count; i++) {
    // Remaining range is [i, working.length). random() in [0, 1)
    // means floor((working.length - i) * r) lands in [0, remaining).
    const remaining = working.length - i;
    const offset = Math.floor(random() * remaining);
    const pickIndex = i + offset;
    const chosen = working[pickIndex]!;
    // Swap chosen into slot i so the next iteration samples from the
    // untouched tail. This is the standard partial-shuffle trick.
    working[pickIndex] = working[i]!;
    working[i] = chosen;
    picked.push(chosen);
  }
  return picked;
}
